import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthenticationService, ConfigurationService, HistoryService, HttpService, OpenIDConnectService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { isContentfulString, isEmptyObject } from '@philips-internal/oswald-ui-utils';
import { doesRouteMatch } from '../util/index.js';
const urlPathRegex = /^https?:\/\/[^\\/]+(\/[^?#]*)/;
function openIdConnectError(message, error) {
    if (error instanceof Error) {
        message = `${message} ${error.message}`;
    }
    return new Error(`[OpenID Connect]: ${message}`);
}
function redirectToLogin(options) {
    const { isExternalRoute, loginRoute, state } = options;
    const isValidState = isContentfulString(state);
    if (isExternalRoute) {
        return HistoryService.redirect(loginRoute);
    }
    const searchParams = isValidState ? { state } : {};
    HistoryService.push(loginRoute, {}, { searchParams });
}
/**
 * Hook used to support with OpenID Connect login and redirect features.
 *
 * Redirects navigation requests to the correct internal or external login route, redirecting to the original route, if the login was successful.
 */
function useOpenIDConnect() {
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const { openIDConnect, routes } = ConfigurationService.getAuthenticationConfiguration() || {};
    useEffect(() => {
        if (!routes?.login) {
            throw openIdConnectError('Missing configuration for login route.');
        }
        const loginRoute = HistoryService.makeURL(routes?.login);
        const redirectToLoginOptions = {
            isExternalRoute: false,
            loginRoute,
        };
        setLoading(true);
        void performOpenIDConnect(redirectToLoginOptions)
            .finally(() => setLoading(false));
    }, [location.pathname]);
    const redirectToErrorRoute = (message, error) => {
        // Using console error here because throw will stop the application
        // eslint-disable-next-line no-console
        console.error(message, error);
        if (!openIDConnect?.errorRoute) {
            return;
        }
        HistoryService.push(openIDConnect.errorRoute, {}, { state: { error } });
    };
    const retrieveLoginOptions = async (baseOptions, state) => {
        if (!openIDConnect || isEmptyObject(openIDConnect) || !isContentfulString(openIDConnect.externalLoginURL)) {
            return baseOptions;
        }
        try {
            const response = await HttpService.get(`${openIDConnect.externalLoginURL}?state=${state.id}`);
            const url = response.headers.get('Location');
            if (isContentfulString(url)) {
                const isInternalRouteURL = url.indexOf(window.location.origin) > -1;
                const pathWithoutParams = url.split(urlPathRegex)[1];
                const isDefaultLoginRoute = isInternalRouteURL && pathWithoutParams === routes?.login;
                if (!isDefaultLoginRoute) {
                    return {
                        ...baseOptions,
                        isExternalRoute: true,
                        loginRoute: url,
                    };
                }
            }
            return baseOptions;
        }
        catch (error) {
            return redirectToErrorRoute('Failed to retrieve external login redirection URL.', error?.body);
        }
    };
    const performOpenIDConnect = async (redirectToLoginOptions) => {
        const isRootPath = location.pathname === '/';
        const queryParams = HistoryService.getCurrentRouteQueryParams();
        const currentState = OpenIDConnectService.getState();
        const isValidStateParam = currentState && OpenIDConnectService.validateState(queryParams?.state);
        const isOpenIDCallbackRoute = location.pathname === openIDConnect?.callbackRoute;
        const isLoginRoute = location.pathname === routes?.login;
        const whitelistedRoutes = { ...routes, callbackRoute: openIDConnect?.callbackRoute, errorRoute: openIDConnect?.errorRoute, home: null };
        const isMatchingRoute = doesRouteMatch(location.pathname + location.search, routes?.custom);
        const isWhitelistedRoute = Object.values(whitelistedRoutes)
            .filter(Boolean)
            .flat()
            .includes(location.pathname);
        const isAllowedRoute = isWhitelistedRoute || isMatchingRoute;
        const isUserAuthenticated = AuthenticationService.isAuthenticated();
        const shouldIgnoreOpenIDConnect = isUserAuthenticated || !isUserAuthenticated && isAllowedRoute && !isLoginRoute && !isRootPath && !isOpenIDCallbackRoute;
        if (shouldIgnoreOpenIDConnect) {
            return;
        }
        if (isOpenIDCallbackRoute) {
            const shouldRedirectToLogin = !currentState && !isValidStateParam || !queryParams?.code;
            if (shouldRedirectToLogin) {
                OpenIDConnectService.clearState();
                return redirectToLogin(redirectToLoginOptions);
            }
            return void AuthenticationService.loginWithAuthorizationCode(String(queryParams.code))
                .then(() => HistoryService.push(currentState.redirectPath))
                .catch((error) => redirectToErrorRoute('Failed to login with authorization code.', error))
                .finally(OpenIDConnectService.clearState);
        }
        const stateAlreadyGenerated = isValidStateParam && queryParams.state === currentState?.id;
        if (stateAlreadyGenerated) {
            return;
        }
        const state = OpenIDConnectService.setState(location.pathname);
        const loginOptions = await retrieveLoginOptions(redirectToLoginOptions, state) ?? {};
        redirectToLogin({ ...redirectToLoginOptions, ...loginOptions, state: state.id });
    };
    return loading;
}
export { useOpenIDConnect, };
//# sourceMappingURL=use-openid-connect.js.map